/**
 * Shell de layout para rutas administrativas (Sprint 11 · 1, ADR-016).
 *
 * Diferente del `AppShell` regular del funcionario municipal:
 *
 * - **Sin** "MUNICIPIO ACTIVO" en el header — el staff observa todos
 *   los tenants, no opera bajo uno.
 * - **Sin** sidebar tenant-scope (Radar, Expedientes, Alertas…) —
 *   el sidebar admin tiene rutas internas (Resumen + futuras Tenants,
 *   Fuentes, Scrapers, Suscripciones).
 * - **Con** link "Vista municipal" cuando el usuario también es member
 *   de algún tenant — útil para el founder que es staff + tester de
 *   Herveo.
 *
 * Bloqueo al no-staff (issue #579). Antes el shell no miraba `is_staff`:
 * cada pagina mostraba "Acceso restringido" recien cuando su primera
 * query recibia 403, asi que el no-staff veia el menu admin completo y,
 * en `/admin/asistente` (que solo llama al backend al enviar), el
 * composer entero. Ahora el shell decide al montar, con el contexto de
 * `/onboarding/context`:
 *
 * - Mientras carga: estado neutro, sin nada del panel.
 * - No staff: la tarjeta `AdminAccessDenied` en todas las rutas admin,
 *   sin la navegacion admin y con una salida al portal municipal.
 * - Contexto caido (la query fallo y no hay datos): no se sabe si es
 *   staff, asi que no se bloquea y decide el backend, como antes.
 *   Bloquear ahi dejaria al staff sin panel justo cuando algo falla.
 *
 * El backend sigue siendo la puerta real (403, R-SEC-008); esto es UX.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";

import { AdminAccessDenied } from "@/components/AdminAccessDenied";
import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

interface AdminNavItem {
  to: string;
  label: string;
  /** Visible pero deshabilitado (sección futura). */
  disabled?: boolean;
}

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { to: "/admin", label: "Resumen" },
  { to: "/admin/analytics", label: "Analytics" },
  { to: "/admin/asistente", label: "Asistente" },
  { to: "/admin/asistente-auditoria", label: "Auditoría IA" },
  { to: "/admin/staff", label: "Staff" },
  { to: "/admin/convocatorias", label: "Convocatorias" },
  { to: "/admin/tenants", label: "Tenants" },
  { to: "/admin/fuentes", label: "Fuentes" },
  { to: "/admin/scrapers", label: "Scrapers" },
  { to: "/admin/suscripciones", label: "Suscripciones", disabled: true },
];

export function AdminShell({ children }: { children?: ReactNode }) {
  const { isStaff, isLoading, context, error } = useMunicipality();

  if (isLoading) return <AdminShellLoading />;

  // Sin datos del contexto no se sabe si es staff: pasa y decide el 403 del
  // backend (ver docstring del modulo).
  const contextUnavailable = context === null && error !== null;
  if (!isStaff && !contextUnavailable) return <AdminShellDenied />;

  return <StaffAdminShell>{children}</StaffAdminShell>;
}

/**
 * Mientras llega el contexto no se sabe si el usuario es staff: ni el panel
 * ni el bloqueo. Mismo "Cargando" que `ProtectedRoute` y `OnboardingGate`,
 * asi la espera se ve como una sola.
 */
function AdminShellLoading() {
  return (
    <div
      role="status"
      data-testid="admin-shell-loading"
      className="flex min-h-screen items-center justify-center bg-surface"
    >
      <p className="text-sm text-slate-500">Cargando…</p>
    </div>
  );
}

/**
 * Lo que ve el no-staff en cualquier ruta `/admin/*`: la tarjeta y una
 * salida al portal municipal (mismo estilo de enlace que el pie de
 * `TenantPausado`). Sin sidebar, sin header del panel.
 */
function AdminShellDenied() {
  return (
    <main
      data-testid="admin-shell-denied"
      className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-10"
    >
      <div className="w-full">
        <AdminAccessDenied />
      </div>
      <nav aria-label="Salir del panel admin" className="mt-4 text-xs">
        <Link
          to="/radar"
          className="inline-flex min-h-11 items-center px-2 font-semibold text-brand-700 hover:underline"
        >
          Ir al portal municipal
        </Link>
      </nav>
    </main>
  );
}

function StaffAdminShell({ children }: { children?: ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { context, daneCode, name } = useMunicipality();
  const hasTenant = Boolean(context?.has_tenant && daneCode);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  // Cerrar drawer al cambiar de ruta — ajustado durante el render, no en un
  // efecto. Ver la nota equivalente en AppShell.
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [drawerOpen, closeDrawer]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <AdminSidebar
        currentPath={location.pathname}
        items={ADMIN_NAV_ITEMS}
        className="hidden md:flex"
        hasTenant={hasTenant}
        municipalityName={name}
      />

      {drawerOpen && (
        <>
          <div
            aria-hidden="true"
            onClick={closeDrawer}
            className="fixed inset-0 z-40 bg-slate-900/60 md:hidden"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-drawer-title"
            className="fixed inset-y-0 left-0 z-50 flex w-60 max-w-[80vw] md:hidden"
          >
            <AdminSidebar
              currentPath={location.pathname}
              items={ADMIN_NAV_ITEMS}
              className="flex w-full shadow-2xl"
              onItemClick={closeDrawer}
              hasTenant={hasTenant}
              municipalityName={name}
              closeButton={
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Cerrar navegación"
                  className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                >
                  ✕
                </button>
              }
            />
          </div>
        </>
      )}

      <div className="flex flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir navegación"
            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 md:hidden"
          >
            <span aria-hidden="true">☰</span>
          </button>

          <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
            Panel administrativo
          </span>

          <div className="ml-auto flex items-center gap-3 text-xs text-slate-600">
            {hasTenant && (
              <Link
                to="/radar"
                data-testid="admin-shell-tenant-link"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 transition hover:border-brand-400 hover:text-brand-700"
                title={name ? `Ir al portal municipal de ${name}` : "Ir al portal municipal"}
              >
                Vista municipal
              </Link>
            )}
            <span className="hidden sm:inline" data-testid="admin-shell-user-email">
              {user?.email ?? "—"}
              <span className="ml-1 text-[10px] tracking-wide text-brand-700 uppercase">staff</span>
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 transition hover:border-red-400 hover:text-red-700"
            >
              Cerrar sesión
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-4 sm:px-6 sm:py-6">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}

function AdminSidebar({
  currentPath,
  items,
  className = "",
  onItemClick,
  closeButton,
  hasTenant,
  municipalityName,
}: {
  currentPath: string;
  items: AdminNavItem[];
  className?: string;
  onItemClick?: () => void;
  closeButton?: ReactNode;
  hasTenant: boolean;
  municipalityName: string | null;
}) {
  return (
    <aside
      aria-label="Navegación admin"
      className={"w-60 shrink-0 flex-col border-r border-slate-200 bg-white px-4 py-6 " + className}
    >
      <div className="mb-8 flex items-start justify-between px-2">
        <div>
          <p
            id="admin-drawer-title"
            className="text-lg font-extrabold tracking-tight text-brand-700"
          >
            kaptar
          </p>
          <p className="text-[11px] font-medium tracking-wide text-slate-500 uppercase">
            Operación interna
          </p>
        </div>
        {closeButton}
      </div>

      <nav className="flex flex-col gap-1">
        {items.map((item) => {
          if (item.disabled) {
            return (
              <span
                key={item.label}
                aria-disabled="true"
                className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-slate-400"
              >
                {item.label}
                <span className="text-[10px] tracking-wide text-slate-300 uppercase">Pronto</span>
              </span>
            );
          }
          const isActive = currentPath === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onItemClick}
              aria-current={isActive ? "page" : undefined}
              className={
                "rounded-md px-3 py-2 text-sm font-medium transition " +
                (isActive ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-50")
              }
            >
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {hasTenant && (
        <div className="mt-auto pt-6 text-[11px] text-slate-500">
          <p className="mb-1 font-semibold tracking-wide text-slate-400 uppercase">
            También eres miembro de
          </p>
          <p className="text-slate-700">{municipalityName ?? "tu municipio"}</p>
          <Link
            to="/radar"
            onClick={onItemClick}
            className="mt-1 inline-block text-brand-700 hover:underline"
          >
            Ir al portal →
          </Link>
        </div>
      )}
    </aside>
  );
}
